import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus, Loader2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useApi, useAdminSession } from "@/lib/api";
import { ShipmentsTable } from "@/components/admin/ShipmentsTable";
import { ShipmentDialog } from "@/components/admin/ShipmentDialog";
import { EventDialog } from "@/components/admin/EventDialog";
import { ProofDialog } from "@/components/admin/ProofDialog";
import { UsersTab } from "@/components/admin/UsersTab";
import { MessagesTab } from "@/components/admin/MessagesTab";
import { ChatsTab, useUnreadChats } from "@/components/admin/ChatsTab";
import { AccountTab, AdminLogout } from "@/components/admin/AccountTab";

const Stat = ({ label, value, testId, accent }) => (
  <div className={`rounded-sm p-6 border ${accent ? "bg-[#FF6B00] border-[#FF6B00] text-white" : "bg-white border-slate-200"}`}>
    <p className={`text-xs uppercase tracking-wider ${accent ? "text-white/80" : "text-slate-500"}`}>{label}</p>
    <p className="font-display text-3xl font-bold mt-2" data-testid={testId}>{value ?? "-"}</p>
  </div>
);

function AdminPanel({ session }) {
  const api = useApi();
  const unread = useUnreadChats();
  const shipments = useQuery({ queryKey: ["admin-shipments"], queryFn: () => api("get", "/admin/shipments"), staleTime: 0 });
  const stats = useQuery({ queryKey: ["admin-stats"], queryFn: () => api("get", "/admin/stats"), staleTime: 0 });
  const [edit, setEdit] = useState({ open: false, shipment: null });
  const [event, setEvent] = useState({ open: false, shipment: null });
  const [proof, setProof] = useState({ open: false, shipment: null });
  const refresh = () => { shipments.refetch(); stats.refetch(); };
  const s = stats.data || {};

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div><p className="text-xs font-mono uppercase tracking-[0.2em] text-[#FF6B00]">Admin</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0A192F]">Consignment control</h1></div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm text-slate-500" data-testid="admin-signed-in-as">Signed in as <b className="text-[#0A192F]">{session.name}</b></span>
          {session.via === "password" && <AdminLogout />}
          <button onClick={() => setEdit({ open: true, shipment: null })} className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0A192F] hover:bg-[#112240] text-white font-semibold text-sm rounded-sm" data-testid="admin-new-shipment-btn"><Plus className="h-4 w-4" /> New shipment</button>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Total consignments" value={s.total} testId="admin-stat-total" />
        <Stat label="Pending approval" value={s.pending} testId="admin-stat-pending" accent />
        <Stat label="Active" value={s.active} testId="admin-stat-active" />
        <Stat label="Delivered" value={s.delivered} testId="admin-stat-delivered" />
      </div>
      <Tabs defaultValue="shipments">
        <TabsList className="flex-wrap h-auto"><TabsTrigger value="shipments" data-testid="tab-shipments">Shipments</TabsTrigger>
          <TabsTrigger value="chats" data-testid="tab-chats">Live chat{unread > 0 && <span className="ml-2 px-1.5 rounded-full bg-[#FF6B00] text-white text-xs" data-testid="chat-unread-badge">{unread}</span>}</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger><TabsTrigger value="messages" data-testid="tab-messages">Messages</TabsTrigger>
          {session.via === "password" && <TabsTrigger value="account" data-testid="tab-account">Account</TabsTrigger>}</TabsList>
        <TabsContent value="shipments" className="mt-4">
          {shipments.isLoading ? <Loader2 className="h-6 w-6 animate-spin text-slate-400" /> :
            <ShipmentsTable rows={shipments.data || []} refresh={refresh} onEdit={(sh) => setEdit({ open: true, shipment: sh })} onEvent={(sh) => setEvent({ open: true, shipment: sh })} onProof={(sh) => setProof({ open: true, shipment: sh })} />}
        </TabsContent>
        <TabsContent value="chats" className="mt-4"><ChatsTab /></TabsContent>
        <TabsContent value="users" className="mt-4"><UsersTab /></TabsContent>
        <TabsContent value="messages" className="mt-4"><MessagesTab /></TabsContent>
        <TabsContent value="account" className="mt-4"><AccountTab /></TabsContent>
      </Tabs>
      <ShipmentDialog open={edit.open} shipment={edit.shipment} onOpenChange={(o) => setEdit({ ...edit, open: o })} onSaved={refresh} />
      <ProofDialog open={proof.open} shipment={proof.shipment} onOpenChange={(o) => setProof({ ...proof, open: o })} onSaved={refresh} />
      <EventDialog open={event.open} shipment={event.shipment} onOpenChange={(o) => setEvent({ ...event, open: o })} onSaved={refresh} />
    </div>
  );
}

export default function AdminPage() {
  const { data: session, isLoading, isError } = useAdminSession();
  if (isError) return <Navigate to="/admin/login" replace />;
  if (isLoading || !session) return <div className="p-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  return <AdminPanel session={session} />;
}
